// ---------------------------------------------------------------------------
// maskRefine.js — background-removal mask-refinement brush (spec G, STRETCH).
//
// Flow: user selects an image -> "Rafineaza masca" -> we run imgly
// `segmentForeground` (via backgroundRemoval.segmentForegroundMask) to get an
// editable alpha mask, then let the user restore/erase the cutout with a soft
// brush and see a live before/after. "Aplica" commits via editor.replaceImage;
// "Renunta" discards. Nothing is blindly replaced.
//
// This owns the 'mask' canvas MODE exclusively (editor.enterMode('mask', ...)),
// so Escape (stage 1) closes it and entering it auto-exits crop/magic-erase.
// The actual brushing happens on a SELF-CONTAINED dialog <canvas> at a capped
// working resolution — deliberately decoupled from the live fabric viewport so
// there is zero zoom/pan coordinate math (the risky part an agent can't eyeball).
// The committed cutout is produced by the SAME manual composite the dialog
// previews (destination-in of the edited alpha mask), guaranteeing preview ==
// result. `applyForegroundMask` (imgly) is available in backgroundRemoval.js but
// intentionally not used for the commit, to keep WYSIWYG fidelity.
//
// Registers: 1 tool button ("Rafineaza masca") + 1 overlay (the dialog) + the
// 'mask' mode. No fabric canvas events are bound here (no object:moving / no
// path:created ownership) — the dialog canvas is a plain DOM canvas.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { registerOverlay } from './overlayHost';
import { segmentForegroundMask, DEFAULT_BG_QUALITY } from './backgroundRemoval';

const WORK_MAX = 1200;   // cap the editable mask resolution (perf / memory)
const UNDO_CAP = 8;

function loadBitmap(blob) {
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

// A grayscale mask (white = keep) -> an alpha-encoded canvas (opaque white where
// keep, transparent where removed) so compositing is pure GPU drawImage.
async function maskBlobToAlpha(blob, w, h) {
  const bmp = await loadBitmap(blob);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  ctx.drawImage(bmp, 0, 0, w, h);
  const id = ctx.getImageData(0, 0, w, h);
  const d = id.data;
  for (let i = 0; i < d.length; i += 4) { const v = d[i]; d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = v; }
  ctx.putImageData(id, 0, 0);
  if (bmp.close) bmp.close();
  return c;
}

// Fallback when segmentForeground is unavailable: seed the mask from the image's
// own alpha (already-cutout images keep their contour; opaque images = all-keep).
function alphaFromImageCanvas(imgWork) {
  const w = imgWork.width, h = imgWork.height;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  ctx.drawImage(imgWork, 0, 0);
  const id = ctx.getImageData(0, 0, w, h);
  const d = id.data;
  for (let i = 0; i < d.length; i += 4) { d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; }
  ctx.putImageData(id, 0, 0);
  return c;
}

function MaskRefineDialog({ editor }) {
  const [target, setTarget] = useState(null);   // non-null => dialog open
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const [applying, setApplying] = useState(false);
  const [mode, setMode] = useState('erase');     // 'erase' | 'restore'
  const [brushSize, setBrushSize] = useState(40);
  const [softEdge, setSoftEdge] = useState(35);
  const [showOriginal, setShowOriginal] = useState(false);
  const [canUndo, setCanUndo] = useState(false);

  const displayRef = useRef(null);
  const st = useRef({});

  // ---- imperative canvas helpers (read ONLY st.current, closure-independent) --
  const maskCtx = () => st.current.maskWork.getContext('2d');

  const paint = useCallback(() => {
    const s = st.current;
    const disp = displayRef.current;
    if (!s.imgWork || !disp) return;
    const ctx = disp.getContext('2d');
    const { wW, wH, imgWork, maskWork, scratch } = s;
    ctx.clearRect(0, 0, wW, wH);
    if (s.showOriginal) {
      ctx.drawImage(imgWork, 0, 0);
    } else {
      const sc = scratch.getContext('2d');
      sc.globalCompositeOperation = 'source-over';
      sc.clearRect(0, 0, wW, wH);
      sc.drawImage(imgWork, 0, 0);
      sc.globalCompositeOperation = 'destination-in';
      sc.drawImage(maskWork, 0, 0);
      sc.globalCompositeOperation = 'source-over';
      ctx.drawImage(scratch, 0, 0);
    }
    if (s.cursor) {
      const { x, y, r } = s.cursor;
      ctx.save();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.beginPath(); ctx.arc(x, y, Math.max(0.5, r - 1.5), 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
    }
  }, []);

  const brushStamp = (ctx, x, y) => {
    const s = st.current;
    const r = Math.max(1, s.brushSize / 2);
    const soft = (s.softEdge || 0) / 100;
    const col = s.mode === 'restore' ? '255,255,255' : '0,0,0';
    ctx.save();
    ctx.globalCompositeOperation = s.mode === 'restore' ? 'source-over' : 'destination-out';
    if (soft <= 0.01) {
      ctx.fillStyle = `rgba(${col},1)`;
    } else {
      const hard = Math.min(0.98, Math.max(0, 1 - soft));
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(${col},1)`);
      g.addColorStop(hard, `rgba(${col},1)`);
      g.addColorStop(1, `rgba(${col},0)`);
      ctx.fillStyle = g;
    }
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const strokeTo = (p) => {
    const s = st.current;
    const from = s.lastPt || p;
    const dx = p.x - from.x, dy = p.y - from.y;
    const dist = Math.hypot(dx, dy);
    const step = Math.max(1, (s.brushSize / 2) * 0.25);
    const n = Math.max(1, Math.ceil(dist / step));
    const ctx = maskCtx();
    for (let i = 1; i <= n; i++) brushStamp(ctx, from.x + (dx * i) / n, from.y + (dy * i) / n);
  };

  const pushUndo = () => {
    const s = st.current;
    if (!s.maskWork) return;
    try {
      const snap = s.maskWork.getContext('2d').getImageData(0, 0, s.wW, s.wH);
      s.undo = s.undo || [];
      s.undo.push(snap);
      if (s.undo.length > UNDO_CAP) s.undo.shift();
      setCanUndo(true);
    } catch (e) { /* getImageData can throw only if tainted — masks never are */ }
  };

  const toWork = (e) => {
    const disp = displayRef.current;
    const rect = disp.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * st.current.wW,
      y: ((e.clientY - rect.top) / rect.height) * st.current.wH,
    };
  };

  // ---- teardown (the ONLY thing 'mask' mode's onExit does — no re-exitMode) ---
  const teardown = useCallback(() => {
    setTarget(null); setReady(false); setLoading(false);
    setApplying(false); setShowOriginal(false); setCanUndo(false);
    st.current = {};
  }, []);

  const prepareDisplay = useCallback(() => {
    const s = st.current;
    const disp = displayRef.current;
    if (!disp) return;
    disp.width = s.wW; disp.height = s.wH;
    const maxW = Math.min(680, window.innerWidth - 120);
    const maxH = window.innerHeight * 0.6;
    const scale = Math.min(maxW / s.wW, maxH / s.wH, 1);
    disp.style.width = `${Math.round(s.wW * scale)}px`;
    disp.style.height = `${Math.round(s.wH * scale)}px`;
    s.scratch = document.createElement('canvas');
    s.scratch.width = s.wW; s.scratch.height = s.wH;
  }, []);

  const beginSegmentation = useCallback(async (obj) => {
    try {
      const imgEl = obj._element;
      if (!imgEl) throw new Error('Imaginea nu are element rasterizat');
      const natW = imgEl.naturalWidth || imgEl.width;
      const natH = imgEl.naturalHeight || imgEl.height;

      // Source blob straight from the loaded element (no fetch / no CORS issue —
      // it already renders on the live canvas, so it is not tainted).
      const srcCanvas = document.createElement('canvas');
      srcCanvas.width = natW; srcCanvas.height = natH;
      srcCanvas.getContext('2d').drawImage(imgEl, 0, 0, natW, natH);
      const blob = await new Promise((res, rej) => srcCanvas.toBlob((b) => (b ? res(b) : rej(new Error('toBlob null'))), 'image/png'));

      const workScale = Math.min(1, WORK_MAX / Math.max(natW, natH));
      const wW = Math.max(1, Math.round(natW * workScale));
      const wH = Math.max(1, Math.round(natH * workScale));
      const imgWork = document.createElement('canvas');
      imgWork.width = wW; imgWork.height = wH;
      imgWork.getContext('2d').drawImage(imgEl, 0, 0, wW, wH);

      let maskWork;
      try {
        const maskBlob = await segmentForegroundMask({
          blob,
          quality: DEFAULT_BG_QUALITY,
          onProgress: ({ phase, pct }) => setProgress(phase === 'download' ? Math.round(pct * 0.5) : 50 + Math.round(pct * 0.5)),
        });
        maskWork = await maskBlobToAlpha(maskBlob, wW, wH);
      } catch (segErr) {
        console.warn('segmentForeground indisponibil, folosesc alpha existent:', segErr);
        maskWork = alphaFromImageCanvas(imgWork);
        editor.toast('Nu am putut genera masca automat. Editeaz-o manual cu pensula.', 'info');
      }

      Object.assign(st.current, {
        target: obj, imgEl, natW, natH, wW, wH, imgWork, maskWork, undo: [],
        mode: 'erase', brushSize: 40, softEdge: 35, showOriginal: false, cursor: null, painting: false,
      });
      // The <canvas> only mounts once loading flips off; prepareDisplay + first
      // paint happen in the `ready` effect below, after it exists in the DOM.
      setLoading(false);
      setReady(true);
    } catch (e) {
      console.error('Rafinarea mastii a esuat:', e);
      editor.toast('Nu am putut initia rafinarea mastii.', 'error');
      editor.exitMode('mask');
    }
  }, [editor]);

  // Register the imperative opener once. enterMode('mask') claims the mode; its
  // onExit runs teardown ONLY (all close paths funnel through exitMode).
  useEffect(() => {
    editor.overlays.mask = (obj) => {
      if (!obj || obj.type !== 'image') { editor.toast('Selecteaza o imagine mai intai.', 'info'); return; }
      editor.enterMode('mask', { onExit: teardown });
      setTarget(obj);
      setReady(false); setApplying(false); setLoading(true); setProgress(0);
      setMode('erase'); setBrushSize(40); setSoftEdge(35); setShowOriginal(false); setCanUndo(false);
      // Defer so the dialog + <canvas> exist before we prepare the display.
      setTimeout(() => beginSegmentation(obj), 0);
    };
    return () => { if (editor.overlays) delete editor.overlays.mask; };
  }, [editor, teardown, beginSegmentation]);

  // Once the <canvas> is mounted (ready), size it to the working resolution and
  // paint the first composite.
  useEffect(() => {
    if (!ready) return;
    prepareDisplay();
    paint();
  }, [ready, prepareDisplay, paint]);

  // Mirror brush params into st.current for the imperative canvas code + repaint.
  useEffect(() => {
    st.current.mode = mode;
    st.current.brushSize = brushSize;
    st.current.softEdge = softEdge;
    st.current.showOriginal = showOriginal;
    if (ready) paint();
  }, [mode, brushSize, softEdge, showOriginal, ready, paint]);

  if (!target) return null;

  const onPointerDown = (e) => {
    if (!ready) return;
    e.preventDefault();
    try { displayRef.current.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
    pushUndo();
    st.current.painting = true;
    const p = toWork(e);
    st.current.lastPt = p;
    st.current.cursor = { x: p.x, y: p.y, r: brushSize / 2 };
    strokeTo(p);
    st.current.lastPt = p;
    paint();
  };
  const onPointerMove = (e) => {
    if (!ready) return;
    const p = toWork(e);
    st.current.cursor = { x: p.x, y: p.y, r: brushSize / 2 };
    if (st.current.painting) { strokeTo(p); st.current.lastPt = p; }
    paint();
  };
  const onPointerUp = (e) => {
    st.current.painting = false;
    try { displayRef.current.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
  };
  const onPointerLeave = () => { st.current.cursor = null; if (ready) paint(); };

  const undoMask = () => {
    const s = st.current;
    if (!s.undo || !s.undo.length) return;
    s.maskWork.getContext('2d').putImageData(s.undo.pop(), 0, 0);
    setCanUndo(s.undo.length > 0);
    paint();
  };

  const regenerate = () => {
    if (!st.current.target) return;
    setReady(false); setLoading(true); setProgress(0); setCanUndo(false);
    setTimeout(() => beginSegmentation(st.current.target), 0);
  };

  const apply = () => {
    const s = st.current;
    if (!s.maskWork || !s.target) { editor.exitMode('mask'); return; }
    setApplying(true);
    try {
      const out = document.createElement('canvas');
      out.width = s.natW; out.height = s.natH;
      const octx = out.getContext('2d');
      octx.drawImage(s.imgEl, 0, 0, s.natW, s.natH);
      octx.globalCompositeOperation = 'destination-in';
      octx.drawImage(s.maskWork, 0, 0, s.wW, s.wH, 0, 0, s.natW, s.natH);
      octx.globalCompositeOperation = 'source-over';
      const url = out.toDataURL('image/png');
      editor.replaceImage(
        s.target, url,
        { name: `${s.target.name || 'Imagine'} (masca)`, label: 'Rafinare masca' },
        () => { setApplying(false); editor.exitMode('mask'); },
      );
    } catch (e) {
      console.error('Aplicarea mastii a esuat:', e);
      editor.toast('Aplicarea mastii a esuat.', 'error');
      setApplying(false);
    }
  };

  const close = () => editor.exitMode('mask');

  return (
    <div className="de-ov-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="de-ov-dialog de-mask-dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="de-ov-header">
          <h3>Rafineaza masca</h3>
          <button className="de-ov-close" onClick={close} title="Inchide">&#10005;</button>
        </div>
        <div className="de-ov-body">
          <div className="de-mask-stage">
            {loading ? (
              <div className="de-mask-loading">
                Se genereaza masca...
                <div className="de-mask-progress-track"><div className="de-mask-progress-fill" style={{ width: `${progress}%` }} /></div>
              </div>
            ) : (
              <canvas
                ref={displayRef}
                className="de-mask-canvas"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerLeave}
              />
            )}
          </div>

          {ready && (
            <div className="de-mask-tools">
              <div className="de-seg">
                <button className={mode === 'erase' ? 'active' : ''} onClick={() => setMode('erase')} title="Sterge zone din imagine">Sterge</button>
                <button className={mode === 'restore' ? 'active' : ''} onClick={() => setMode('restore')} title="Reface zone eliminate">Reface</button>
              </div>
              <div className="de-mask-slider">
                <span className="de-label">Pensula</span>
                <input type="range" min="4" max="160" step="1" value={brushSize} onChange={(e) => setBrushSize(parseInt(e.target.value, 10))} className="de-range" />
                <span className="de-val">{brushSize}</span>
              </div>
              <div className="de-mask-slider">
                <span className="de-label">Margine</span>
                <input type="range" min="0" max="100" step="1" value={softEdge} onChange={(e) => setSoftEdge(parseInt(e.target.value, 10))} className="de-range" />
                <span className="de-val">{softEdge}</span>
              </div>
              <button
                className={`de-btn de-btn-sm ${showOriginal ? 'active' : ''}`}
                onMouseDown={() => setShowOriginal(true)}
                onMouseUp={() => setShowOriginal(false)}
                onMouseLeave={() => setShowOriginal(false)}
                title="Tine apasat ca sa vezi imaginea originala"
              >Vezi original</button>
            </div>
          )}
        </div>
        <div className="de-ov-footer">
          <span className="de-ov-footer-note">Picteaza pe imagine ca sa ajustezi conturul.</span>
          <button className="de-btn" onClick={undoMask} disabled={!canUndo || applying} title="Un pas inapoi">Pas inapoi</button>
          <button className="de-btn" onClick={regenerate} disabled={loading || applying} title="Regenereaza masca automata">Regenereaza</button>
          <button className="de-btn" onClick={close} disabled={applying}>Renunta</button>
          <button className="de-btn de-btn-primary" onClick={apply} disabled={!ready || applying}>{applying ? 'Se aplica...' : 'Aplica'}</button>
        </div>
      </div>
    </div>
  );
}

export function registerMaskRefine(editor) {
  registerOverlay(editor, 'mask', MaskRefineDialog);
  editor.addToolButton({
    id: 'de-mask-refine',
    title: 'Rafineaza masca (AI) - editeaza manual conturul decupajului',
    icon: 'Rafineaza masca',
    isActive: () => editor.isMode('mask'),
    onClick: (ed) => {
      const o = ed.getActive();
      if (!o || o.type !== 'image') { ed.toast('Selecteaza o imagine mai intai.', 'info'); return; }
      if (ed.overlays && ed.overlays.mask) ed.overlays.mask(o);
    },
  });
}
